import type { Agenda } from "@repo/types";
import type { ColumnDef } from "@tanstack/react-table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/Base/DropDown";
import { Button } from "@repo/ui/components/Base/Button";
import { MoreHorizontal, SquareArrowOutUpRight } from "lucide-react";
import { RoleProtected } from "../common/RoleProtected";
import {
  createCreatedAtColumn,
  createCreatedByColumn,
  createIdColumn,
  createNameColumn,
  createTeamColumn,
  createUpdatedAtColumn,
  createUpdatedByColumn,
} from "../dataTable/columns";
import type { TableLayoutState } from "@repo/ui/components/Base/DataTable";
import { useUserStore } from "@/stores/userStore";
import { useMemo } from "react";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { cn } from "@repo/shared/utils";

export const useColumns = ({
  tenantId,
  handleEdit,
  handleView: _handleView,
  handleReoccurring: _handleReoccurring,
  handleCopy: _handleCopy,
  handleArchive,
}: {
  tenantId: string;
  handleEdit: (id: string) => void;
  handleView?: (id: string) => void;
  handleReoccurring?: (id: string) => void;
  handleCopy?: (id: string) => void;
  handleArchive: (id: string) => void;
}) => {
  const hadHandleView = !!_handleView;
  const hadHandleReoccurring = !!_handleReoccurring;
  const hadHandleCopy = !!_handleCopy;

  handleEdit = useFutureCallback(handleEdit);
  const handleView = useFutureCallback(_handleView ?? (() => {}));
  const handleReoccurring = useFutureCallback(_handleReoccurring ?? (() => {}));
  const handleCopy = useFutureCallback(_handleCopy ?? (() => {}));
  handleArchive = useFutureCallback(handleArchive);

  const col: ColumnDef<Agenda>[] = useMemo(() => {
    return [
      {
        id: "actions",
        header: "",
        cell: function Cell({ row }) {
          const team = useUserStore((s) => s.activeTeam);
          const isFullOrg = !team || team === "org";

          const agendaTemplate = row.original;
          return (
            <div className="flex flex-row align-middle gap-1 justify-start">
              <Button
                variant="ghost"
                tooltip={
                  isFullOrg ? "Pick a team first" : "Edit the agenda template"
                }
                className={cn(
                  "h-8 w-8 p-0",
                  isFullOrg && "opacity-50 cursor-not-allowed"
                )}
                disabled={isFullOrg}
                onClick={() => handleEdit(agendaTemplate.id)}
              >
                <SquareArrowOutUpRight />
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="h-8 w-8 p-0">
                    <span className="sr-only">Open menu</span>
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuLabel>Actions</DropdownMenuLabel>
                  <RoleProtected minRole="edit">
                    <DropdownMenuSeparator />
                    {hadHandleView && (
                      <DropdownMenuItem
                        onClick={() => handleView(agendaTemplate.id)}
                      >
                        View
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      disabled={isFullOrg}
                      tooltip={isFullOrg ? "Pick a team first" : ""}
                      onClick={() => handleEdit(agendaTemplate.id)}
                    >
                      Edit
                    </DropdownMenuItem>
                    {hadHandleCopy && (
                      <DropdownMenuItem
                        disabled={isFullOrg}
                        tooltip={
                          isFullOrg
                            ? "Pick a team first"
                            : "Copy this template to other team"
                        }
                        onClick={() => handleCopy(agendaTemplate.id)}
                      >
                        Copy to
                      </DropdownMenuItem>
                    )}
                    {hadHandleReoccurring && (
                      <DropdownMenuItem
                        disabled={isFullOrg}
                        tooltip={
                          isFullOrg
                            ? "Pick a team first"
                            : "Create scheduled meetings from this template"
                        }
                        onClick={() => handleReoccurring(agendaTemplate.id)}
                      >
                        Generate Reccurring
                        <br />
                        Meetings from Template
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem
                      onClick={() => handleArchive(agendaTemplate.id)}
                    >
                      Archive
                    </DropdownMenuItem>
                  </RoleProtected>
                  <DropdownMenuItem
                    onClick={() =>
                      navigator.clipboard.writeText(agendaTemplate.id)
                    }
                  >
                    Copy agenda template ID
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableHiding: false,
      },
      createIdColumn(),
      createNameColumn(),
      createCreatedAtColumn(),
      createCreatedByColumn(tenantId),
      createUpdatedAtColumn(),
      createUpdatedByColumn(tenantId),
      createTeamColumn(tenantId),
    ];
  }, [
    tenantId,
    hadHandleView,
    hadHandleCopy,
    hadHandleReoccurring,
    handleView,
    handleEdit,
    handleCopy,
    handleReoccurring,
    handleArchive,
  ]);
  return col;
};

export const layoutDefaults: TableLayoutState = {
  columnVisibility: {
    id: false,
    updatedAt: false,
  },
};
